import { Food } from "@corpuscomplete/core/nutrition/food";
import { FoodItemLabel } from "@corpuscomplete/web/components/nutrition/food-item";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { CommandItem } from "@corpuscomplete/web-ui/components/command";
import type { FoodSearchUI } from "@corpuscomplete/core-ui/nutrition/food/search";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { createContext, useContext, useState } from "react";

type PreparationMenuOpenContextValue = {
  isPreparationMenuOpen: boolean;
  setIsPreparationMenuOpen: (open: boolean) => void;
};

const PreparationMenuOpenContext = createContext<PreparationMenuOpenContextValue | null>(null);

function usePreparationMenuOpenContext(): PreparationMenuOpenContextValue {
  const context = useContext(PreparationMenuOpenContext);
  const [isPreparationMenuOpen, setIsPreparationMenuOpen] = useState(false);
  return context ?? { isPreparationMenuOpen, setIsPreparationMenuOpen };
}

export function FoodSearchPreparationMenuProvider({ children }: { children: React.ReactNode }) {
  const [isPreparationMenuOpen, setIsPreparationMenuOpen] = useState(false);
  return (
    <PreparationMenuOpenContext.Provider
      value={{ isPreparationMenuOpen, setIsPreparationMenuOpen }}
    >
      {children}
    </PreparationMenuOpenContext.Provider>
  );
}

/**
 * Command-list section for interactive food search results.
 * Contract: renders core-ui grouped search output and forwards
 * selected Food.Item payloads unchanged to caller handlers.
 */
export function FoodSearchCommandGroup({
  section,
  onSelectItem,
}: {
  section: FoodSearchUI.Section;
  onSelectItem: (food: Food.Item) => void;
}) {
  const { isPreparationMenuOpen, setIsPreparationMenuOpen } = usePreparationMenuOpenContext();

  return (
    <>
      <div className="flex flex-row items-center justify-between px-1">
        <Text variant="lg" className="font-semibold">
          {section.title}
        </Text>
        {section.expandButton && (
          <Button variant="link" size="sm" onClick={section.expandButton.onToggle}>
            <Text variant="sm">{section.expandButton.label}</Text>
          </Button>
        )}
      </div>
      {section.data.map((row) => (
        <FoodSearchCommandItem
          key={row.key}
          row={row}
          onSelect={onSelectItem}
          disableRowSelection={isPreparationMenuOpen}
          onPreparationMenuOpenChange={setIsPreparationMenuOpen}
        />
      ))}
    </>
  );
}

function FoodSearchCommandItem({
  row,
  onSelect,
  disableRowSelection,
  onPreparationMenuOpenChange,
}: {
  row: FoodSearchUI.Section["data"][number];
  onSelect: (food: Food.Item) => void;
  disableRowSelection: boolean;
  onPreparationMenuOpenChange: (open: boolean) => void;
}) {
  const hasPreparations = row.preparations.length > 0;
  return (
    <CommandItem
      className={cn(
        "flex flex-col items-start gap-0 rounded-none p-1",
        "data-selected:has-[[data-slot=dropdown-menu-trigger]:hover]:bg-transparent",
        "data-selected:has-[[data-slot=dropdown-menu-trigger][data-popup-open]]:bg-transparent",
        "data-[disabled=true]:opacity-100",
      )}
      disabled={disableRowSelection}
      onSelect={() => {
        onSelect(row.item);
      }}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <FoodItemLabel item={row.item} />
        <div className="flex shrink-0 items-center gap-1">
          {hasPreparations && (
            <DropdownMenu onOpenChange={(open) => onPreparationMenuOpenChange(open)}>
              <DropdownMenuTrigger
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <Badge
                  variant="outline"
                  className="hover:bg-gray-100 [[data-slot=dropdown-menu-trigger][data-popup-open]_&]:bg-gray-100"
                >
                  {row.preparationCountLabel}
                </Badge>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-max min-w-max max-w-[calc(100vw-1rem)]">
                {row.preparations.map((preparation) => (
                  <DropdownMenuItem
                    key={preparation.key}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(preparation.item);
                    }}
                  >
                    <FoodItemLabel item={preparation.item} mode="preparation" />
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </CommandItem>
  );
}

/**
 * Suggestions section renderer for quick-add food choices.
 * Contract: receives pre-ranked suggestions and forwards selection upstream.
 */
export function FoodSuggestionsCommandGroup({
  suggestions,
  onSelectItem,
}: {
  suggestions: Food.Item[];
  onSelectItem: (item: Food.Item) => void;
}) {
  const { isPreparationMenuOpen, setIsPreparationMenuOpen } = usePreparationMenuOpenContext();

  return (
    <>
      <div className="flex flex-row items-center justify-between px-1">
        <Text variant="lg" className="font-semibold">
          Suggestions
        </Text>
      </div>
      {suggestions.map((item) => (
        <FoodSearchCommandItem
          key={`${item.food.id}:${item.food.version}`}
          row={{
            key: `${item.food.id}:${item.food.version}`,
            item,
            preparations: [],
            preparationCountLabel: undefined,
          }}
          onSelect={onSelectItem}
          disableRowSelection={isPreparationMenuOpen}
          onPreparationMenuOpenChange={setIsPreparationMenuOpen}
        />
      ))}
    </>
  );
}
